import React, { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Manager global pentru detectarea mouse-ului peste iframe-uri Spline
 * Folosește un singur set de listeners pe window în loc de câte unul per wrapper
 */

// Store global pentru toate wrapper-ele înregistrate
const registeredWrappers = new Set();
let mouseX = 0;
let mouseY = 0;
let isListenerAttached = false;

// Handler-ul global de wheel
const handleGlobalWheel = (e) => {
  // Verifică toate wrapper-ele înregistrate
  for (const wrapper of registeredWrappers) {
    if (!wrapper.current) continue;
    
    const rect = wrapper.current.getBoundingClientRect();
    const isOver = 
      mouseX >= rect.left &&
      mouseX <= rect.right &&
      mouseY >= rect.top &&
      mouseY <= rect.bottom;

    if (isOver) {
      // Mouse-ul e peste acest wrapper - blochează scroll-ul paginii
      e.preventDefault();
      e.stopPropagation();
      return; // Oprește verificarea celorlalte wrapper-e
    }
  }
  // Dacă nu e peste niciun wrapper, permite scroll-ul normal
};

// Handler-ul global de mouse move
const handleGlobalMouseMove = (e) => {
  mouseX = e.clientX;
  mouseY = e.clientY;
};

// Inițializează listeners-urile globale
const initGlobalListeners = () => {
  if (isListenerAttached) return;
  
  window.addEventListener('mousemove', handleGlobalMouseMove, { passive: true });
  window.addEventListener('wheel', handleGlobalWheel, { passive: false });
  isListenerAttached = true;
};

// Cleanup global listeners când nu mai sunt wrapper-e
const cleanupGlobalListeners = () => {
  if (registeredWrappers.size > 0) return;
  
  window.removeEventListener('mousemove', handleGlobalMouseMove);
  window.removeEventListener('wheel', handleGlobalWheel);
  isListenerAttached = false;
};

/**
 * SplineMapWrapper pentru IFRAME-uri
 * Folosește un manager global pentru a evita multiple listeners
 */
const SplineMapWrapperIframe = ({ children, className = "", style = {}, debugMode = false }) => {
  const containerRef = useRef(null);
  const [isMouseOver, setIsMouseOver] = useState(false);

  // Înregistrează/dezînregistrează wrapper-ul
  useEffect(() => {
    // Înregistrează acest wrapper
    registeredWrappers.add(containerRef);
    
    // Inițializează listeners-urile globale (doar prima dată)
    initGlobalListeners();

    // Cleanup la unmount
    return () => {
      registeredWrappers.delete(containerRef);
      cleanupGlobalListeners();
    };
  }, []);

  // Local mouse tracking pentru debug overlay
  useEffect(() => {
    if (!debugMode) return;
    
    const checkMouseOver = () => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const isOver = 
        mouseX >= rect.left &&
        mouseX <= rect.right &&
        mouseY >= rect.top &&
        mouseY <= rect.bottom;
      
      setIsMouseOver(isOver);
    };

    // Update starea la interval pentru debug
    const interval = setInterval(checkMouseOver, 100);
    
    return () => clearInterval(interval);
  }, [debugMode]);

  return (
    <div 
      ref={containerRef} 
      className={`spline-map-wrapper-iframe ${className}`}
      style={{
        ...style,
        position: 'relative',
        isolation: 'isolate'
      }}
      data-spline-wrapper-iframe="true"
    >
      {children}
      
      {debugMode && (
        <div style={{
          position: 'absolute',
          top: 10,
          right: 10,
          background: 'rgba(0,0,0,0.95)',
          color: 'white',
          padding: '12px',
          borderRadius: '6px',
          zIndex: 99999,
          pointerEvents: 'none',
          border: `3px solid ${isMouseOver ? '#00ff00' : '#ff0000'}`,
          fontFamily: 'monospace',
          fontSize: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.8)'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>IFRAME Debug</div>
          <div>Mouse: {isMouseOver ? '✅ OVER' : '❌ OUT'}</div>
          <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '4px' }}>
            Wrappers: {registeredWrappers.size}
          </div>
        </div>
      )}
    </div>
  );
};

export default SplineMapWrapperIframe;
